import React from 'react';
import { PayRate } from '../shared/PayLabel';
import { downloadFile } from '../../utils/api';
import { toast } from '../ui/sonner';
import { AlertCircle, AlertTriangle, ArrowLeftRight, Bell, Briefcase, Calendar, CheckCircle, Clock, DollarSign, Download, Edit3, MapPin, MessageSquare, Play, RefreshCw, Search, Star, UserPlus, XCircle } from 'lucide-react';

// Extracted from WorkerDashboard.js — receives everything it needs via `ctx`.
export default function WorkerMyShiftsTab({ ctx }) {
  const { GPSCheckInOut, ReliabilityBadge, WorkerPaymentClaims, actionLoading, activeTab, alertSetShifts, completedCount, filteredShifts, handleConfirmShift, handleRejectConfirmation, handleRespondToTransfer, handleSetJobAlert, inProgressShifts, jobs, loadAvailableWorkers, loadMyShifts, loadSwitchableShifts, loading, myShifts, openEditShiftModal, openRatingModal, pendingTransfers, rate, ratedJobs, setActiveTab, setShiftFilter, setShowGroupChat, setShowRejectModal, setShowSwitchModal, setShowTransferModal, shiftFilter, upcomingCount } = ctx;
  return (
    <>
    {activeTab === 'my-shifts' && (
      <div className="space-y-6">
        {/* Header - matches legacy */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-100 rounded-xl">
                <Calendar className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">My Shifts <ReliabilityBadge /></h2>
                <p className="text-slate-500 text-sm">Track and manage your assigned work</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  await downloadFile('/api/me/shifts/calendar.ics', 'scensob-shifts.ics');
                  toast.success('Calendar file downloaded — import it into Google/Apple/Outlook calendar');
                } catch (e) { toast.error(e?.message || 'Calendar export failed'); }
              }}
              className="p-3 bg-slate-100 rounded-xl hover:bg-slate-200 transition"
              title="Export shifts to calendar (.ics)"
              data-testid="export-calendar-btn"
            >
              <Download className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={loadMyShifts}
              className="p-3 bg-slate-100 rounded-xl hover:bg-slate-200 transition"
              data-testid="refresh-shifts-btn"
            >
              <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
            </div>
          </div>
        </div>

        {/* Stats Row - 4 cards matching legacy */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <button onClick={() => setShiftFilter('all')} className={`bg-white rounded-xl p-3 md:p-5 border-2 transition text-left min-w-0 ${shiftFilter === 'all' ? 'border-slate-600 ring-1 ring-slate-300' : 'border-gray-200 hover:border-slate-300'}`} data-testid="filter-all-shifts">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800" data-testid="shifts-all-count">{myShifts.length}</p>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">All Shifts</p>
              </div>
            </div>
          </button>
          <button onClick={() => setShiftFilter('in_progress')} className={`bg-white rounded-xl p-3 md:p-5 border-2 transition text-left min-w-0 ${shiftFilter === 'in_progress' ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`} data-testid="filter-in-progress">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <Play className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600" data-testid="shifts-in-progress-count">
                  {inProgressShifts.length}
                </p>
                <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide">In Progress</p>
              </div>
            </div>
          </button>
          <button onClick={() => setShiftFilter('upcoming')} className={`bg-white rounded-xl p-3 md:p-5 border-2 transition text-left min-w-0 ${shiftFilter === 'upcoming' ? 'border-amber-500 ring-1 ring-amber-200' : 'border-gray-200 hover:border-amber-300'}`} data-testid="filter-upcoming">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-600" data-testid="shifts-upcoming-count">
                  {upcomingCount}
                </p>
                <p className="text-xs font-semibold text-amber-500 uppercase tracking-wide">Upcoming</p>
              </div>
            </div>
          </button>
          <button onClick={() => setShiftFilter('completed')} className={`bg-white rounded-xl p-3 md:p-5 border-2 transition text-left min-w-0 ${shiftFilter === 'completed' ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-gray-200 hover:border-emerald-300'}`} data-testid="filter-completed">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-600" data-testid="shifts-completed-count">
                  {completedCount}
                </p>
                <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide">Completed</p>
              </div>
            </div>
          </button>
        </div>

        {/* iter180: only ONE shift can be in progress at a time. If two
            slipped through, surface a bright warning so payroll can
            investigate — a worker can't physically clock into two shifts. */}
        {inProgressShifts.length > 1 && (
          <div
            className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3"
            data-testid="in-progress-conflict-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-semibold">
                {inProgressShifts.length} shifts are showing as In Progress at the same time.
              </p>
              <p className="text-red-700/90 text-xs mt-1">
                Only one shift can be in progress. Please clock out of any duplicates or contact
                your HR / employer to resolve this.
              </p>
            </div>
          </div>
        )}

        {/* Cancelled-shift payment claims — always visible, even with no
            active shifts (June 2026 fix: renders null when empty) */}
        <div className="mb-6"><WorkerPaymentClaims /></div>

        {/* Shifts List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-slate-600 animate-spin" />
          </div>
        ) : myShifts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="inline-flex p-6 bg-slate-100 rounded-2xl mb-4">
              <Calendar className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No Shifts Yet</h3>
            <p className="text-slate-500 mb-6">Your accepted shifts will appear here. Browse available jobs and start earning!</p>
            <button
              onClick={() => setActiveTab('jobs')}
              className="px-6 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 font-medium inline-flex items-center gap-2 transition"
            >
              <Search className="w-5 h-5" />
              Browse Jobs
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pending Transfer Requests */}
            {pendingTransfers.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <h4 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Pending Transfer Requests ({pendingTransfers.length})
                </h4>
                <div className="space-y-3">
                  {pendingTransfers.map(transfer => (
                    <div key={transfer._id} className="bg-white rounded-xl p-4 border border-amber-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 break-words line-clamp-2" title={transfer.job.title}>{transfer.job.title}</p>
                          <p className="text-sm text-gray-500">{transfer.from_worker_name} wants to transfer this shift to you</p>
                          <PayRate job={transfer.job} className="text-sm text-amber-600 mt-1 block" testId="transfer-rate" />
                          {transfer.reason && (
                            <p className="text-sm text-gray-600 mt-2 italic">"{transfer.reason}"</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRespondToTransfer(transfer._id, 'accept')}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium disabled:opacity-50"
                            data-testid={`accept-transfer-${transfer._id}`}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleRespondToTransfer(transfer._id, 'reject')}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium disabled:opacity-50"
                            data-testid={`reject-transfer-${transfer._id}`}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Shifts Grid */}
            {filteredShifts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <p className="text-slate-500">No {shiftFilter === 'all' ? '' : shiftFilter.replace('_', ' ')} shifts found.</p>
                {shiftFilter !== 'all' && (
                  <button onClick={() => setShiftFilter('all')} className="mt-2 text-sm text-indigo-600 hover:underline">Show all shifts</button>
                )}
              </div>
            ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {filteredShifts.map((shift) => (
                <div key={shift._id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition">
                  {/* If shift has GPS check-in data, show full GPSCheckInOut */}
                  {(shift.status === 'assigned' || shift.status === 'confirmed' || shift.status === 'in_progress' || shift.status === 'active') ? (
                    <>
                      {/* Confirmation Banner */}
                      {shift.confirmation_status === 'pending_confirmation' && (
                        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-testid={`confirm-banner-${shift._id}`}>
                          <p className="text-sm font-medium text-amber-800 mb-1">This shift needs your confirmation</p>
                          {shift.confirmation_deadline_at && (() => {
                            const deadline = new Date(shift.confirmation_deadline_at);
                            const msLeft = deadline - new Date();
                            if (msLeft <= 0) return <p className="text-xs text-red-700 font-semibold mb-2" data-testid={`deadline-passed-${shift._id}`}>⚠ Deadline passed — the shift may be re-opened at any moment.</p>;
                            const hrs = Math.floor(msLeft / 3600000);
                            const mins = Math.floor((msLeft % 3600000) / 60000);
                            const label = hrs >= 1 ? `${hrs}h ${mins}m` : `${mins}m`;
                            const urgent = msLeft < 60 * 60 * 1000; // <1h
                            return <p className={`text-xs font-semibold mb-2 ${urgent ? 'text-red-700' : 'text-amber-700'}`} data-testid={`deadline-countdown-${shift._id}`}>
                              ⏱ {label} left to confirm — else the shift is re-opened to other workers.
                            </p>;
                          })()}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmShift(shift._id)}
                              disabled={actionLoading}
                              className="flex-1 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
                              data-testid={`confirm-shift-btn-${shift._id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Confirm Shift
                            </button>
                            <button
                              onClick={() => handleRejectConfirmation(shift._id)}
                              disabled={actionLoading}
                              className="flex-1 py-2 rounded-lg text-sm font-semibold bg-white text-red-600 border border-red-200 hover:bg-red-50 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
                              data-testid={`decline-shift-btn-${shift._id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              Decline
                            </button>
                          </div>
                        </div>
                      )}
                      {shift.confirmation_status === 'confirmed' && (
                        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center gap-2 text-sm text-emerald-700" data-testid={`confirmed-badge-${shift._id}`}>
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-medium">Confirmed</span>
                          {shift.confirmed_at && <span className="text-emerald-500 text-xs ml-auto">{new Date(shift.confirmed_at).toLocaleDateString()}</span>}
                        </div>
                      )}
                      <GPSCheckInOut 
                        job={{...shift, start_time: shift.shift_start || shift.start_time, end_time: shift.shift_end || shift.end_time}}
                        onStatusChange={(status) => {
                          loadMyShifts();
                          toast.success(`Shift status updated: ${status}`);
                        }}
                      />
                      {/* Shift Actions — Reject / Transfer are locked
                          once the shift starts (worker has clocked in)
                          AND once the shift has ended. Group chat stays
                          available while there's correspondence to
                          reference; otherwise it too is disabled. */}
                      {(() => {
                        const isInProgress = shift.status === 'in_progress';
                        const hasEnded = ['completed', 'pending_approval', 'approved', 'paid', 'cancelled'].includes(shift.status);
                        const rejectDisabled = isInProgress || hasEnded;
                        const transferDisabled = hasEnded;         // Reject-in-progress swaps to Switch below
                        const rejectTitle = hasEnded
                          ? 'This shift has ended — reject no longer applies'
                          : isInProgress
                            ? 'Cannot reject a shift you have already started'
                            : '';
                        const transferTitle = hasEnded
                          ? 'This shift has ended — transfer no longer applies'
                          : '';
                        const chatEnabled = shift.has_chat || isInProgress || !hasEnded;
                        const chatTitle = chatEnabled ? '' : 'No group chat history for this shift';
                        return (
                      <div className="px-4 pb-3 flex gap-2">
                        <button
                          onClick={() => setShowRejectModal(shift)}
                          disabled={rejectDisabled}
                          title={rejectTitle}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition text-sm font-medium border ${
                            rejectDisabled
                              ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                              : 'bg-red-50 text-red-600 hover:bg-red-100 border-red-100'
                          }`}
                          data-testid={`reject-shift-${shift._id}`}
                        >
                          <XCircle className="w-4 h-4" />
                          Reject Shift
                        </button>
                        {isInProgress ? (
                          <button
                            onClick={() => { setShowSwitchModal(shift); loadSwitchableShifts(shift._id); }}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition text-sm font-medium border border-indigo-100"
                            data-testid={`switch-shift-${shift._id}`}
                          >
                            <ArrowLeftRight className="w-4 h-4" />
                            Switch Shift
                          </button>
                        ) : (
                          <button
                            onClick={() => { setShowTransferModal(shift); loadAvailableWorkers(shift._id); }}
                            disabled={transferDisabled}
                            title={transferTitle}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition text-sm font-medium border ${
                              transferDisabled
                                ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100'
                            }`}
                            data-testid={`transfer-shift-${shift._id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                            Transfer
                          </button>
                        )}
                        <button
                          onClick={() => chatEnabled && setShowGroupChat(shift)}
                          disabled={!chatEnabled}
                          title={chatTitle}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition text-sm font-medium border ${
                            chatEnabled
                              ? 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                              : 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                          }`}
                          data-testid={`shift-group-chat-${shift._id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Shift Group Chat
                        </button>
                      </div>
                        );
                      })()}
                      {/* Set Job Alert */}
                      <div className="px-4 pb-3">
                        {alertSetShifts[shift._id] ? (
                          <div className="flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium border border-blue-200" data-testid={`alert-set-badge-${shift._id}`}>
                            <Bell className="w-4 h-4" />
                            Job Alert Set
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSetJobAlert(shift)}
                            className="w-full py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-sm font-medium border border-blue-200 flex items-center justify-center gap-2 transition"
                            data-testid={`set-alert-btn-${shift._id}`}
                          >
                            <Bell className="w-4 h-4" />
                            Alert Me for Jobs Like This
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Completed shift card - simpler view */
                    <div>
                      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-bold text-lg break-words line-clamp-2" title={shift.title}>{shift.title}</h3>
                            <p className="text-white/80 text-sm truncate">{shift.employer_name || 'Employer'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <PayRate job={shift} className="text-2xl font-bold block" lockedClassName="!text-sm opacity-90" testId="shift-card-rate" />
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded-full text-xs mt-1">
                              <CheckCircle className="w-3 h-3" /> Completed
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Start</p>
                            <p className="font-medium">{shift.shift_start ? new Date(shift.shift_start).toLocaleDateString([], {month: 'short', day: 'numeric'}) : 'N/A'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Total Pay</p>
                            <p className="font-medium text-emerald-600">${shift.total_pay?.toFixed(2) || '0.00'}</p>
                          </div>
                        </div>
                        <div className="col-span-2 flex items-center gap-2 min-w-0">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <p className="text-gray-600 truncate" title={shift.location?.address}>{shift.location?.address || 'Location not specified'}</p>
                        </div>
                      </div>
                      {/* Rate Job Button */}
                      <div className="px-4 pb-4">
                        {ratedJobs[shift._id] ? (
                          <div className="flex items-center justify-center gap-2 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-medium border border-emerald-200" data-testid={`rated-badge-${shift._id}`}>
                            <CheckCircle className="w-4 h-4" />
                            Rated
                          </div>
                        ) : (
                          <button
                            onClick={() => openRatingModal(shift)}
                            className="w-full py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl text-sm font-medium border border-amber-200 flex items-center justify-center gap-2 transition"
                            data-testid={`rate-job-btn-${shift._id}`}
                          >
                            <Star className="w-4 h-4" />
                            Rate This Job
                          </button>
                        )}
                      </div>
                      {/* Set Job Alert - Completed Shifts */}
                      <div className="px-4 pb-4 -mt-2">
                        {alertSetShifts[shift._id] ? (
                          <div className="flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium border border-blue-200" data-testid={`alert-set-badge-${shift._id}`}>
                            <Bell className="w-4 h-4" />
                            Job Alert Set
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSetJobAlert(shift)}
                            className="w-full py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-sm font-medium border border-blue-200 flex items-center justify-center gap-2 transition"
                            data-testid={`set-alert-btn-${shift._id}`}
                          >
                            <Bell className="w-4 h-4" />
                            Alert Me for Jobs Like This
                          </button>
                        )}
                      </div>
                      {/* iter188: Edit Shift button on the completed
                          layout. Only shown when the 14-day window is
                          still open (or HR/admin unlocked). Falls back
                          to a disabled locked-state box for the "past
                          window" case so the worker knows why. */}
                      {shift.edit_status && (
                        <div className="px-4 pb-4 -mt-2">
                          {shift.edit_status.editable ? (
                            <button
                              onClick={() => openEditShiftModal(shift)}
                              className="w-full py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl text-sm font-medium border border-amber-200 flex items-center justify-center gap-2 transition"
                              data-testid={`edit-completed-shift-${shift._id}`}
                            >
                              <Edit3 className="w-4 h-4" />
                              Edit Shift / Add Notes
                              <span className="text-xs text-amber-700/80">
                                ({Math.max(0, (shift.edit_status.window_days || 14) - shift.edit_status.days_since_checkout)}d left)
                              </span>
                            </button>
                          ) : (
                            <div
                              className="w-full py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-500 text-[11px] flex items-start gap-2 px-3"
                              data-testid={`edit-locked-${shift._id}`}
                            >
                              <Edit3 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>Edit window closed — contact HR / admin to reopen.</span>
                            </div>
                          )}
                        </div>
                      )}
                      {/* iter186: kept for reference on ended shifts —
                          only rendered when there's actually chat history
                          on this shift. If no chat, the button stays off
                          (Reject/Transfer are already hidden for the
                          completed layout). */}
                      {shift.has_chat && (
                        <div className="px-4 pb-4 -mt-2">
                          <button
                            onClick={() => setShowGroupChat(shift)}
                            className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-medium border border-slate-200 flex items-center justify-center gap-2 transition"
                            data-testid={`shift-group-chat-${shift._id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                            View Group Chat
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        )}

      </div>
    )}
    </>
  );
}
